import React, { ChangeEvent, useState } from 'react';
import Button from 'src/components/Button';
import LoadingButton from 'src/components/BulkOperations/_Shared/BulkActionButton/LoadingButton';
import ModernSelect, { ModernSelectOption } from 'src/components/ModernSelect';
import Callout from 'src/components/InformationCallout/Callout';
import ConfirmationModal from 'src/components/ConfirmationModal/ConfirmationModal';
import FontAwesomeIcon from 'src/components/Icon';
import { ICalcomEventType } from '../../types';
import {
  buildEventTypeOptions,
  findEventTypeOption,
} from './eventTypeOptions';
import { CALCOM_SELECT_STYLES } from '../../calcomSelectStyles';
import { findKindNameMatch, KindNameMatch } from './eventTypeKindMatch';
import './booking-link-confirm.scss';

export interface BookingLinkFormValues {
  label: string;
  calcomEventTypeId: number;
}

interface BookingLinkFormProps {
  eventTypes: ICalcomEventType[];
  kindLabel: string;
  submitLabel: string;
  isSubmitting: boolean;
  onSubmit: (values: BookingLinkFormValues) => void;
  onCancel: () => void;
  initialLabel?: string;
  initialEventTypeId?: number;
  labelPlaceholder?: string;
  isEditing: boolean;
}

const BookingLinkForm = ({
  eventTypes,
  kindLabel,
  submitLabel,
  isSubmitting,
  onSubmit,
  onCancel,
  initialLabel = '',
  initialEventTypeId,
  labelPlaceholder,
  isEditing,
}: BookingLinkFormProps) => {
  const [label, setLabel] = useState<string>(initialLabel);
  const [eventTypeId, setEventTypeId] = useState<number>(
    initialEventTypeId ?? eventTypes[0]?.id ?? 0
  );
  const [isConfirming, setIsConfirming] = useState<boolean>(false);

  const eventTypeOptions: ModernSelectOption[] =
    buildEventTypeOptions(eventTypes);

  const selectedEventTypeOption: ModernSelectOption | null =
    findEventTypeOption(eventTypeOptions, eventTypeId);

  const selectedEventType: ICalcomEventType | null =
    eventTypes.find(
      (eventType: ICalcomEventType) => eventType.id === eventTypeId
    ) ?? null;

  const kindLower: string = kindLabel.toLowerCase();

  const eventTypeNameMatch: KindNameMatch | null = selectedEventType
    ? findKindNameMatch(selectedEventType.title, kindLabel)
    : null;

  const nameLooksRight: boolean = eventTypeNameMatch !== null;

  const isValid: boolean = label.trim() !== '' && eventTypeId > 0;

  const selectEventType = (option: ModernSelectOption | null): void => {
    if (option) {
      setEventTypeId(Number(option.value));
    }
  };

  const requestConfirm = (): void => {
    setIsConfirming(true);
  };

  const cancelConfirm = (): void => {
    setIsConfirming(false);
  };

  const submit = (): void => {
    setIsConfirming(false);
    onSubmit({ label, calcomEventTypeId: eventTypeId });
  };

  return (
    <>
      <div className="row-fields">
        <label className="row-field">
          <span className="row-field-label">Label</span>
          <input
            className="row-input"
            type="text"
            value={label}
            placeholder={labelPlaceholder}
            onChange={(event: ChangeEvent<HTMLInputElement>) =>
              setLabel(event.target.value)
            }
          />
        </label>

        <label className="row-field">
          <span className="row-field-label">
            Cal.com event type which belongs to my account
          </span>
          {isEditing ? (
            <span className="row-input-readonly">
              {selectedEventTypeOption?.label ?? '—'}
            </span>
          ) : (
            <ModernSelect
              size="regular"
              options={eventTypeOptions}
              value={selectedEventTypeOption}
              onChange={(option: ModernSelectOption | null) =>
                selectEventType(option)
              }
              styles={CALCOM_SELECT_STYLES}
            />
          )}

          {!isEditing && selectedEventType && (
            <div className="event-type-match-callout">
              <Callout
                variant={nameLooksRight ? 'success' : 'warning'}
                icon={
                  <FontAwesomeIcon
                    glyph={
                      nameLooksRight ? 'check-circle' : 'exclamation-triangle'
                    }
                    weight="solid"
                  />
                }
                title={
                  nameLooksRight
                    ? `Looks like a valid ${kindLower} event type :)`
                    : `This event type isn't named for ${kindLower}s :(`
                }
                subtitle={
                  nameLooksRight ? undefined : (
                    <>
                      Its Cal.com name doesn&apos;t include &ldquo;{kindLower}
                      &rdquo;, so recipients won&apos;t see what the invite is
                      for and it&apos;s harder to tell apart from your other booking
                      links. You can still use it — we just don&apos;t recommend
                      it.
                    </>
                  )
                }
              >
                {!nameLooksRight && (
                  <div className="event-type-match-fix">
                    <p className="event-type-match-fix-label">To fix this:</p>
                    <ol>
                      <li>
                        Pick a different event type below whose name already
                        includes &ldquo;{kindLower}&rdquo;, or
                      </li>
                      <li>
                        If none exists yet, create one in Cal.com with &ldquo;
                        {kindLower}&rdquo; in its name (create a new event type
                        rather than renaming an existing one), then refresh this
                        page to pull it in.
                      </li>
                    </ol>
                  </div>
                )}
              </Callout>
            </div>
          )}
        </label>
      </div>

      {isEditing && (
        <Callout
          variant="info"
          title="Event type is locked for existing booking links"
          subtitle="Cal.com ties every booking to the event type it was created under, so switching it here would break in-flight bookings and calendar syncs. To offer a different service, create a new booking link instead."
        />
      )}

      <div className="booking-link-form-actions">
        <Button outlined size="small" onClick={() => onCancel()}>
          Cancel
        </Button>
        {isEditing ? (
          <LoadingButton
            actionText={submitLabel}
            isSubmitting={isSubmitting}
            disabled={!isValid}
            onClick={() => submit()}
            size="small"
          />
        ) : (
          <Button
            color="teal"
            size="small"
            disabled={!isValid}
            onClick={() => requestConfirm()}
          >
            {submitLabel}
          </Button>
        )}
      </div>

      {isConfirming && (
        <ConfirmationModal
          variant={nameLooksRight ? 'confirmation' : 'warning'}
          className="booking-link-confirm"
          title={
            nameLooksRight
              ? 'This looks right :)'
              : "This doesn't look right :("
          }
          confirmLabel={nameLooksRight ? submitLabel : `${submitLabel} anyway`}
          cancelLabel="Back"
          isSubmitting={isSubmitting}
          onCancel={() => cancelConfirm()}
          onConfirm={() => submit()}
          message={
            <>
              <p className="confirm-lead">
                {nameLooksRight ? (
                  <>
                    You&apos;re about to create this {kindLower} booking link:
                  </>
                ) : (
                  <>
                    You&apos;re about to create this {kindLower} booking link —
                    but its event type name doesn&apos;t mention &ldquo;
                    {kindLower}&rdquo;:
                  </>
                )}
              </p>
              <ul className="confirm-summary">
                <li>
                  <span className="confirm-summary-key">Link name</span>
                  <span className="confirm-summary-value">{label}</span>
                </li>
                <li>
                  <span className="confirm-summary-key">Cal.com event type</span>
                  <span
                    className={`confirm-summary-value ${
                      nameLooksRight ? '' : 'confirm-summary-value--mismatch'
                    }`}
                  >
                    {eventTypeNameMatch ? (
                      <>
                        {eventTypeNameMatch.before}
                        <span className="confirm-match">
                          {eventTypeNameMatch.match}
                        </span>
                        {eventTypeNameMatch.after}
                      </>
                    ) : (
                      selectedEventType?.title
                    )}
                  </span>
                </li>
              </ul>
            </>
          }
          warningMessage={
            nameLooksRight ? (
              <>
                Once you add this link, its event type is locked. Cal.com binds
                every booking to the event type it was created under, so
                changing it later would break {kindLower}s already booked and
                the links already shared. To switch event type, you&apos;d
                remove this link and add a new one.
              </>
            ) : (
              <>
                Its name doesn&apos;t include &ldquo;{kindLower}&rdquo;, so
                recipients won&apos;t see what the invite is for and it&apos;s
                harder to tell apart from your other booking links. You can still create
                it — but are you sure? We don&apos;t recommend it.
              </>
            )
          }
        />
      )}
    </>
  );
};

export default BookingLinkForm;